import { CustomerService } from '@rightship-tech/common-ui/services/basic/customer-client.service';
import { autoinject } from "aurelia-dependency-injection";
import { Router } from "aurelia-router";
import { DocumentRequirementDocumentModel, DocumentRequirementFormModel, DocumentRequirementModel } from "../../../../../models/document-management.models";
import { ValidationController, ValidationControllerFactory, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "@rightship-tech/common-ui/client-validation-system/custom-validation-renderer";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DocumentManagementDialogConstants } from "../../../../../constants/dialog.constants";
import { DocumentRequirementApi } from "../../../../../apis/document-requirement.api";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { Claims } from '@rightship-tech/common-ui/constants/authentication/claims';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { LookupValue } from '@rightship-tech/common-ui/models/lookup.models';

@autoinject
export class AddDocumentRequirements {

    private _documentRequirementId: number;
    private _validator: ValidationController;
    public isEditing: boolean;
    public documentRequirement: DocumentRequirementModel;
    public customerId: number;
    public isRsUser: boolean = false;
    public selectedVesselTypeGroup: LookupValue;

    constructor(private router: Router, private dialogClientService: DialogClientService, private documentRequirementApi: DocumentRequirementApi,
        private notificationClientService: NotificationClientService, private customerService: CustomerService, 
        private googleAnalyticsService: GoogleAnalyticsService, validationControllerFactory: ValidationControllerFactory,
        private userClaimsService: UserClaimsService) {
            this._validator = validationControllerFactory.createForCurrentScope();
            this._validator.addRenderer(new CustomValidationRenderer());
            this.isRsUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    activate(params: { documentRequirementId: string, isEditing: string }) {
        this._documentRequirementId = +params.documentRequirementId;
        this.isEditing = params.isEditing == "true";
    }

    async attached() {

        //Get customer id
        this.customerId = await this.customerService.getCustomerId();
        
        //Load document requirement if not empty and isEditing
        if (this._documentRequirementId && this.isEditing) {
            this.documentRequirement = await this.documentRequirementApi.getMyDocumentRequirement(this._documentRequirementId);
            this.documentRequirement.visibilityAuthorized = false;
        }
        else {
            this.documentRequirement = {
                documentRequirementId: 0,
	            customerId: 0,
                createdBy: 0,
                modifiedBy: 0,
                customerName: this.isRsUser ? "RightShip" : "",
                name: "",
                description: "",
                visibilityAuthorized: false,
                isDeleted: false,
                documentRequirementDocuments: [],
                documentRequirementForms: []
            }
            this.addDocumentRequirement();
            this.addFormRequirement();
        }
    }

    //Save
    public async save() {
        if(this.documentRequirement.documentRequirementDocuments.length == 0 && this.documentRequirement.documentRequirementForms.length == 0) {
            this.notificationClientService.show("Please add at least one document or form requirement", "", NotificationType.Error);
            return;
        }

        //Get rules
        const rules = this.getRules();
        let valid = (await this._validator.validate({ object: this.documentRequirement, rules: rules })).valid;

        //Rules for docs
        for(const doc of this.documentRequirement.documentRequirementDocuments) {
            const docRules = this.getRulesForDocs();
            const docValidationResult = await this._validator.validate({ object: doc, rules: docRules });
            valid = valid && docValidationResult.valid;
        }

        //Rules for forms
        for(const form of this.documentRequirement.documentRequirementForms) {
            const formRules = this.getRulesForForms();
            const formValidationResult = await this._validator.validate({ object: form, rules: formRules });
            valid = valid && formValidationResult.valid;
        }
        
        if (valid) {
            
            try {
                await this.documentRequirementApi.saveDocumentRequirement(this.documentRequirement);
                this.notificationClientService.show(`Document requirement ${this.isEditing ? 'saved':'added'} successfully`, "", NotificationType.Success);

                //Send event to google analytics
                this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_save_button", {
                    "vesselTypeGroup": this.selectedVesselTypeGroup?.name
                });
                
                //Navigate back
                this.router.navigateBack();
            }
            catch (error) {
                console.error(error);
                this.notificationClientService.show("Error occured", "", NotificationType.Error);
            }
        }
    }

    //Cancel
    public cancel() {

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_cancel_button", {
            "vesselTypeGroup": this.selectedVesselTypeGroup?.name
        });

        //Navigate back
        this.router.navigateBack();
    }

    //Open remove document requirement confirmation dialog
    public openRemoveRequirementConfirmationDailog() {
        this.dialogClientService.show(DocumentManagementDialogConstants.DeleteDocumentRequirementConfirmationDialog,
        {
            documentRequirementName: this.documentRequirement?.name ?? "N/a",
            documentRequirementId: this.documentRequirement?.documentRequirementId,
            onDelete: (() => {
                this.router.navigateBack();
            }).bind(this)
        }, true);
    }

    //Add document requirement
    public addDocumentRequirement() {
        this.documentRequirement.documentRequirementDocuments.push({
            documentRequirementDocumentId: 0,
            documentTypeId: 0,
            documentValidityInDays: 0,
            description: ""
        });

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_add_document_requirement_button", {
            "vesselTypeGroup": this.selectedVesselTypeGroup?.name
        });
    }

    //Remove document requirement
    public removeDocumentRequirement(index: number) {
        this.documentRequirement.documentRequirementDocuments.splice(index, 1);

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_remove_document_requirement_button", {
            "vesselTypeGroup": this.selectedVesselTypeGroup?.name
        });
    }

    //Add form requirement
    public addFormRequirement() {
        this.documentRequirement.documentRequirementForms.push({
            documentRequirementFormId: 0,
            formTypeId: 0,
            documentValidityInDays: 0,
            description: ""
        });

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_add_form_requirement_button", {
            "vesselTypeGroup": this.selectedVesselTypeGroup?.name
        });
    }

    //Remove form requirement
    public removeFormRequirement(index: number) {
        this.documentRequirement.documentRequirementForms.splice(index, 1);

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_remove_form_requirement_button", {
            "vesselTypeGroup": this.selectedVesselTypeGroup?.name
        });
    }
    
    //Get rules for validations
    private getRules() {
        return ValidationRules
            .ensure<DocumentRequirementModel, any>(x => x.name)
            .required()
            .withMessage("Name is required")

            .ensure(x => x.customerName)
            .required()
            .withMessage("Customer name is required")

            .ensure(x => x.visibilityAuthorized)
            .equals(true)
            .withMessage("Please tick the box to authorize that RightShip can make these requirements visible on the platform")
            .rules;
    }

    //Get rules for validations
    private getRulesForDocs() {
        return ValidationRules
            .ensure<DocumentRequirementDocumentModel, any>(x => x.documentTypeId)
            .satisfies(x => x > 0)
            .withMessage("Document type is required")
            .rules;
    }

    //Get rules for validations
    private getRulesForForms() {
        return ValidationRules
            .ensure<DocumentRequirementFormModel, any>(x => x.formTypeId)
            .satisfies(x => x > 0)
            .withMessage("Form type is required")
            .rules;
    }

    //Visiblity authorized checkbox click
    public visibilityAuthorizedClick() {
        if (this.documentRequirement.visibilityAuthorized) {

            //Send event to google analytics
            this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_add_requirement_visibility_authorized_checkbox", {
                "vesselTypeGroup": this.selectedVesselTypeGroup?.name
            });
        }
    }
}
